import { useQuery } from '@tanstack/react-query'
import {
	CalendarDays,
	CheckCircle2,
	Clock,
	Globe,
	Mail,
	Monitor,
	ShieldCheck,
	User,
	XCircle,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { Avatar } from '@/components/admin/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { usePageTitle } from '@/hooks/use-page-title'
import { adminApi, type AdminMe } from '@/lib/admin-api'
import { cn } from '@/utils/misc'

export function AdminProfilePage() {
	usePageTitle('Profil')

	const meQuery = useQuery({
		queryKey: ['admin', 'me'],
		queryFn: adminApi.me,
		staleTime: 5 * 60_000,
	})

	const me = meQuery.data

	return (
		<div className="space-y-8">
			<header>
				<h1 className="text-2xl font-bold tracking-tight text-gray-900">
					Profil
				</h1>
				<p className="text-sm text-gray-500 mt-1">
					Informace o vašem administrátorském účtu
				</p>
			</header>

			{/* Identity card */}
			<section className="rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
				<div className="flex flex-col sm:flex-row sm:items-center gap-5">
					{meQuery.isPending ? (
						<Skeleton className="h-20 w-20 rounded-full" />
					) : (
						<Avatar
							src={me?.image}
							name={me?.name}
							email={me?.email}
							size="lg"
						/>
					)}
					<div className="min-w-0 flex-1">
						{meQuery.isPending ? (
							<>
								<Skeleton className="h-6 w-48 mb-2" />
								<Skeleton className="h-4 w-64" />
							</>
						) : (
							<>
								<h2 className="text-xl font-semibold text-gray-900 truncate">
									{me?.name || me?.username || 'Administrátor'}
								</h2>
								<p className="text-sm text-gray-500 mt-1 truncate">
									{me?.email}
								</p>
								<div className="mt-3 flex flex-wrap items-center gap-2">
									{me?.emailVerified ? (
										<Badge tint="green">
											<CheckCircle2 className="h-3 w-3" />
											Email ověřen
										</Badge>
									) : (
										<Badge tint="amber">
											<XCircle className="h-3 w-3" />
											Email neověřen
										</Badge>
									)}
									{me?.providers.map((p) => (
										<Badge key={p} tint="neutral">
											<ShieldCheck className="h-3 w-3" />
											{formatProvider(p)}
										</Badge>
									))}
								</div>
							</>
						)}
					</div>
				</div>
			</section>

			{/* Account details */}
			<section>
				<h3 className="text-sm font-semibold text-gray-900 mb-3">Účet</h3>
				<dl className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
					<DetailRow
						icon={User}
						label="Uživatelské ID"
						value={me?.id}
						loading={meQuery.isPending}
						mono
					/>
					<DetailRow
						icon={Mail}
						label="Email"
						value={me?.email}
						loading={meQuery.isPending}
					/>
					<DetailRow
						icon={User}
						label="Uživatelské jméno"
						value={me?.username || '—'}
						loading={meQuery.isPending}
					/>
					<DetailRow
						icon={CalendarDays}
						label="Účet vytvořen"
						value={me ? formatDateTime(me.createdAt) : undefined}
						loading={meQuery.isPending}
					/>
					<DetailRow
						icon={Clock}
						label="Poslední aktualizace"
						value={me ? formatDateTime(me.updatedAt) : undefined}
						loading={meQuery.isPending}
					/>
				</dl>
			</section>

			{/* Session */}
			<section>
				<h3 className="text-sm font-semibold text-gray-900 mb-3">Relace</h3>
				<dl className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
					<DetailRow
						icon={CalendarDays}
						label="Přihlášení"
						value={me ? formatDateTime(me.session.createdAt) : undefined}
						loading={meQuery.isPending}
					/>
					<DetailRow
						icon={Clock}
						label="Platnost do"
						value={me ? formatDateTime(me.session.expiresAt) : undefined}
						loading={meQuery.isPending}
					/>
					<DetailRow
						icon={Globe}
						label="IP adresa"
						value={me?.session.ipAddress || '—'}
						loading={meQuery.isPending}
						mono
					/>
					<DetailRow
						icon={Monitor}
						label="Zařízení"
						value={me ? formatUserAgent(me.session.userAgent) : undefined}
						loading={meQuery.isPending}
					/>
				</dl>
			</section>

			{meQuery.isError && (
				<div className="rounded-xl border border-red-200 bg-red-50 p-4">
					<p className="text-red-700 text-sm">
						Could not load profile information.
					</p>
				</div>
			)}
		</div>
	)
}

const BADGE_TINTS = {
	green: 'bg-green-50 text-green-700 border-green-100',
	amber: 'bg-amber-50 text-amber-700 border-amber-100',
	neutral: 'bg-gray-50 text-gray-700 border-gray-200',
} as const

function Badge({
	children,
	tint,
}: {
	children: ReactNode
	tint: keyof typeof BADGE_TINTS
}) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
				BADGE_TINTS[tint],
			)}
		>
			{children}
		</span>
	)
}

function DetailRow({
	icon: Icon,
	label,
	value,
	loading,
	mono,
}: {
	icon: typeof User
	label: string
	value: string | undefined
	loading: boolean
	mono?: boolean
}) {
	return (
		<div className="flex items-start gap-4 px-5 py-4">
			<div className="mt-0.5 shrink-0">
				<Icon className="h-4 w-4 text-gray-400" />
			</div>
			<div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4">
				<dt className="text-sm text-gray-500">{label}</dt>
				<dd
					className={cn(
						'sm:col-span-2 text-sm text-gray-900 break-words',
						mono && 'font-mono text-xs',
					)}
				>
					{loading ? <Skeleton className="h-4 w-40" /> : value}
				</dd>
			</div>
		</div>
	)
}

function formatProvider(p: string): string {
	if (p === 'google') return 'Google'
	if (p === 'credential') return 'Email / heslo'
	return p.charAt(0).toUpperCase() + p.slice(1)
}

function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString(undefined, {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function formatUserAgent(ua: string | null): string {
	if (!ua) return '—'
	// Trim ultra-long UA strings but keep the recognisable prefix.
	return ua.length > 80 ? `${ua.slice(0, 80)}…` : ua
}

// Re-exported to avoid TS "unused" on the type when this file is extended later.
export type { AdminMe }
